import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Save, Wrench } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const DEFAULT_COEFFICIENTS = [
  { work_type: 'dirty', coefficient: 2, description: 'Грязная работа' },
  { work_type: 'clean', coefficient: 1, description: 'Чистая работа' }
];

export default function MechanicCoefficients() {
  const [coefficients, setCoefficients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCoefficients();
  }, []);

  const fetchCoefficients = async () => {
    try {
      const response = await axios.get(`${API}/api/work-coefficients`, getAuthHeaders());
      const existing = response.data || [];

      // Ensure both dirty and clean exist
      const result = DEFAULT_COEFFICIENTS.map(def => {
        const found = existing.find(c => c.work_type === def.work_type);
        return found || { ...def, id: null };
      });

      setCoefficients(result);
    } catch (error) {
      console.error('Error fetching coefficients:', error);
      toast.error('Ошибка загрузки коэффициентов');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const coeff of coefficients) {
        if (coeff.id) {
          await axios.put(`${API}/api/work-coefficients/${coeff.id}`, {
            coefficient: coeff.coefficient,
            description: coeff.description
          }, getAuthHeaders());
        } else {
          await axios.post(`${API}/api/work-coefficients`, {
            work_type: coeff.work_type,
            coefficient: coeff.coefficient,
            description: coeff.description
          }, getAuthHeaders());
        }
      }
      toast.success('Коэффициенты сохранены');
      fetchCoefficients();
    } catch (error) {
      console.error('Error saving coefficients:', error);
      toast.error('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const updateCoeff = (workType, field, value) => {
    setCoefficients(prev =>
      prev.map(c =>
        c.work_type === workType
          ? { ...c, [field]: field === 'coefficient' ? parseFloat(value) || 0 : value }
          : c
      )
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <Card className="shadow-lg" data-testid="mechanic-coefficients">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Wrench className="w-5 h-5 text-orange-600" />
          Коэффициенты оплаты механика
        </CardTitle>
        <p className="text-sm text-gray-500">Ставки за час работы. Механик указывает время при закрытии задачи.</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {coefficients.map(coeff => (
          <div
            key={coeff.work_type}
            className={`p-4 rounded-xl border-2 ${
              coeff.work_type === 'dirty'
                ? 'border-orange-200 bg-orange-50'
                : 'border-blue-200 bg-blue-50'
            }`}
            data-testid={`coeff-${coeff.work_type}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-3 h-3 rounded-full ${
                coeff.work_type === 'dirty' ? 'bg-orange-500' : 'bg-blue-500'
              }`} />
              <span className="font-bold text-gray-800">
                {coeff.work_type === 'dirty' ? 'Грязная работа' : 'Чистая работа'}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-gray-600">Ставка (EUR/час)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={coeff.coefficient}
                  onChange={(e) => updateCoeff(coeff.work_type, 'coefficient', e.target.value)}
                  className="mt-1 bg-white"
                  data-testid={`coeff-rate-${coeff.work_type}`}
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600">Описание</Label>
                <Input
                  value={coeff.description}
                  onChange={(e) => updateCoeff(coeff.work_type, 'description', e.target.value)}
                  className="mt-1 bg-white"
                />
              </div>
            </div>
          </div>
        ))}

        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold"
          data-testid="save-coefficients-button"
        >
          {saving ? 'Сохранение...' : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Сохранить коэффициенты
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
